import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BaseCoreState, BaseDateService, ShiftPlanCalendarDateInterface, baseInquirySelectors } from 'base';
import { ShiftPlanMdGutterGroupInterface } from '../../../../../../_interfaces/base-customer-shift-plan-md.interface';
import { buildShiftPlanMdGutterGroups } from '../../../../../../_helpers/base-customer-shift-plan-md-gutter.helper';
import { BaseCustomerSimilarInquiriesMdScrollService } from '../../../../../../_services/base-customer-similar-inquiries-md-scroll.service';

/**
 * Date gutter of the mobile similar-inquiries panel: calendar week and day, over the same day
 * range as the organizer, so the panel's inquiry columns line up row-for-row with it. The month
 * gutter the organizer carries is dropped here — the day cells spell the full date instead.
 *
 * The body still scrolls natively (so wheel and touch keep working), but its scrollbar is hidden
 * in favour of the slider, which mirrors the offset in both directions.
 */
@Component({
  selector: 'base-customer-similar-inquiries-md-calendar',
  templateUrl: './base-customer-similar-inquiries-md-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseCustomerSimilarInquiriesMdCalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollBody') scrollBody: ElementRef<HTMLElement>;
  @ViewChild('scrollGrid') scrollGrid: ElementRef<HTMLElement>;

  days: ShiftPlanCalendarDateInterface[] = [];
  weekGroups: ShiftPlanMdGutterGroupInterface[] = [];

  scrollTop = 0;
  scrollMax = 0;

  private destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;

  constructor(
    private store: Store<BaseCoreState>,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private scrollService: BaseCustomerSimilarInquiriesMdScrollService
  ) {}

  ngOnInit(): void {
    this.scrollService.scrollTop$.pipe(takeUntil(this.destroy$)).subscribe((scrollTop) => {
      const element = this.scrollBody?.nativeElement;

      // Sub-pixel differences are the browser's own rounding of the offset this box already holds;
      // writing them back would bounce the shared value between the columns.
      if (element && Math.abs(element.scrollTop - scrollTop) >= 1) {
        element.scrollTop = scrollTop;
      }
    });

    this.store
      .select(baseInquirySelectors.getShiftPlanList)
      .pipe(
        takeUntil(this.destroy$),
        map((data) => data.list as ShiftPlanCalendarDateInterface[])
      )
      .subscribe((days) => {
        this.days = days;
        this.weekGroups = buildShiftPlanMdGutterGroups(
          days,
          ({ dateItem }) => `CW${BaseDateService.weekNumber(dateItem.dateString)}`
        );
      });
  }

  /**
   * Both boxes are observed: the grid grows as days arrive, and the body's own height changes
   * whenever the panel slides open or shut — neither of which raises a window resize.
   */
  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.measure()));
    this.resizeObserver.observe(this.scrollBody.nativeElement);
    this.resizeObserver.observe(this.scrollGrid.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBodyScroll(): void {
    this.scrollTop = this.scrollBody.nativeElement.scrollTop;
    this.scrollService.setScrollTop(this.scrollTop);
    this.cdr.markForCheck();
  }

  /**
   * The resulting scroll event feeds scrollTop back, so the slider needs no separate update.
   *
   * At the far end the slider is handed an offset past the bottom for the browser to clamp:
   * clientHeight is an integer while the body's real height is fractional (the panel's top is
   * measured off the organizer), so scrollMax under-reports by up to a pixel — enough to leave
   * a sliver of the last row's border showing.
   */
  onScrollChange(value: number): void {
    const element = this.scrollBody.nativeElement;

    element.scrollTop = value >= this.scrollMax ? element.scrollHeight : value;
  }

  trackByGroup(_: number, item: ShiftPlanMdGutterGroupInterface): string {
    return item.gridRow;
  }

  trackByDay(_: number, item: ShiftPlanCalendarDateInterface): string {
    return item.dateItem.dateString;
  }

  private measure(): void {
    const element = this.scrollBody.nativeElement;

    this.scrollMax = Math.max(0, element.scrollHeight - element.clientHeight);
    this.scrollTop = element.scrollTop;
    this.cdr.markForCheck();
  }
}
